import React, { useEffect, useState, useMemo, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import { qaqcCategories } from '../../data/mockData';
import LinkedCorrespondenceCard from '../../components/LinkedCorrespondenceCard';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import StatusBadge from '../../components/StatusBadge';
import { printTableElement } from '../../utils/exportUtils';
import { qaqcAPI } from '../../services/authService';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../utils/attachmentStorage';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN } from '../../utils/dateFormat';
import { useFormConfiguration } from '../../hooks/useFormConfiguration';
import { evaluateConfigRules, missingRequiredFromEffects } from '../../utils/configRuleEval';
import { DynamicFieldsSection } from '../../components/DynamicFieldRenderer';
import { adminAPI } from '../../services/authService';

const QAQC_RESULT_CLASS_MAP = {
  Pass: 'approved',
  Fail: 'rejected',
  Conditional: 'expiring',
};

const QAQC_STATUS_CLASS_MAP = {
  Approved: 'approved',
  Rework: 'rejected',
  'Under Review': 'under-review',
};

const QAQC = () => {
  const [projectFilter, setProjectFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [qaqcItems, setQaqcItems] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [newInspection, setNewInspection] = useState({
    project_id: '',
    category: 'General',
    activity: '',
    inspection_date: '',
    inspector: '',
    parameters_checked: '',
    result: 'Pass',
    retest_date: '',
    defects_found: '',
    corrective_action: '',
    remarks: '',
    attachments: [],
  });
  const [customValues, setCustomValues] = useState({});
  const [detailCustomValues, setDetailCustomValues] = useState({});
  const tableRef = useRef(null);
  const formConfig = useFormConfiguration({ pageKey: 'qaqc', formKey: 'qaqc_inspection', mode: editingId ? 'edit' : 'create' });
  const fl = formConfig.labelFor;
  const ph = formConfig.placeholderFor;
  const hideBase = formConfig.isHidden;
  const reqBase = formConfig.isRequired;
  const customFields = formConfig.customFields || [];
  const ruleEffects = useMemo(
    () => (formConfig.rulesEnabled ? evaluateConfigRules(formConfig.rules, newInspection) : {}),
    [formConfig.rulesEnabled, formConfig.rules, newInspection],
  );
  const ruleHidden = (fieldKey) => Boolean(ruleEffects[fieldKey]?.hidden);
  const ruleRequired = (fieldKey) => Boolean(ruleEffects[fieldKey]?.required);
  const ruleReadOnly = (fieldKey) => Boolean(ruleEffects[fieldKey]?.readOnly || ruleEffects[fieldKey]?.disabled);
  const hide = (fieldKey) => hideBase(fieldKey) || (formConfig.rulesEnabled && ruleHidden(fieldKey));
  const req = (fieldKey, defaultRequired = false) => {
    if (formConfig.rulesEnabled && ruleRequired(fieldKey)) return true;
    return reqBase(fieldKey, defaultRequired);
  };
  const readOnlyField = (fieldKey) => formConfig.rulesEnabled && ruleReadOnly(fieldKey);

  const blankInspection = () => ({
    project_id: '',
    category: 'General',
    activity: '',
    inspection_date: '',
    inspector: '',
    parameters_checked: '',
    result: 'Pass',
    retest_date: '',
    defects_found: '',
    corrective_action: '',
    remarks: '',
    attachments: [],
  });

  const defaultsFromCustomFields = () => {
    const out = {};
    for (const f of customFields) {
      if (f.defaultValue != null && f.defaultValue !== '') out[f.fieldKey] = f.defaultValue;
      else if (f.fieldType === 'boolean') out[f.fieldKey] = false;
      else out[f.fieldKey] = '';
    }
    return out;
  };

  const onCustomChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  };

  useEffect(() => {
    const loadQaqc = async () => {
      try {
        const response = await qaqcAPI.getAll({ page: 1, pageSize: 100 });
        const { rows: rawRows } = unwrapListResponse(response);
        const rows = safeArray(rawRows).map((row) => {
          const payload = row.data || {};
          return {
            id: row.id,
            project_id: row.projectId ?? row.project_id ?? 1,
            checklist_no: payload.checklist_no ?? row.checklist_no ?? `QC-${row.id}`,
            activity: row.activity || payload.activity || '-',
            category: payload.category ?? row.category ?? 'General',
            inspection_date: payload.inspection_date ?? row.inspection_date ?? '',
            inspector: payload.inspector ?? row.inspector ?? '-',
            result: row.result || payload.result || 'Conditional',
            parameters_checked: payload.parameters_checked ?? row.parameters_checked ?? '',
            defects_found: payload.defects_found ?? row.defects_found ?? '',
            corrective_action: payload.corrective_action ?? row.corrective_action ?? '',
            retest_date: payload.retest_date ?? row.retest_date ?? null,
            status: row.status || payload.status || 'Under Review',
            remarks: payload.remarks ?? row.remarks ?? '',
            linked_correspondence: payload.linked_correspondence ?? row.linked_correspondence ?? null,
            attachments: payload.attachments ?? row.attachments ?? [],
          };
        });
        if (rows.length > 0) setQaqcItems(rows);
      } catch (error) {
        console.warn('QAQC API unavailable, using local data:', error?.message || error);
      } finally {
        setDataReady(true);
      }
    };
    loadQaqc();
  }, []);

  useEffect(() => {
    if (!selectedItem?.id || !formConfig.customFieldsEnabled) {
      setDetailCustomValues({});
      return undefined;
    }
    let cancelled = false;
    adminAPI.getCustomFieldValues('QAQCItem', selectedItem.id)
      .then((res) => {
        if (cancelled) return;
        setDetailCustomValues(res?.data?.data?.values || res?.data?.values || {});
      })
      .catch(() => { if (!cancelled) setDetailCustomValues({}); });
    return () => { cancelled = true; };
  }, [selectedItem?.id, formConfig.customFieldsEnabled]);
  useEffect(() => {
    if (!formConfig.rulesEnabled) return;
    setNewInspection((prev) => {
      let next = prev;
      let changed = false;
      Object.entries(ruleEffects).forEach(([key, e]) => {
        if (e.clear && prev[key]) {
          if (!changed) { next = { ...prev }; changed = true; }
          next[key] = '';
        } else if (e.defaultValue != null && (prev[key] === undefined || prev[key] === null || prev[key] === '')) {
          if (!changed) { next = { ...prev }; changed = true; }
          next[key] = e.defaultValue;
        }
      });
      return changed ? next : prev;
    });
  }, [ruleEffects, formConfig.rulesEnabled]);


  const projects = safeArray(projectOptions);

  const filteredItems = useMemo(() => {
    let items = [...safeArray(qaqcItems)];
    if (projectFilter !== 'all') items = items.filter(i => i.project_id === parseInt(projectFilter));
    if (categoryFilter !== 'all') items = items.filter(i => i.category === categoryFilter);
    if (resultFilter !== 'all') items = items.filter(i => i.result === resultFilter);
    return items;
  }, [qaqcItems, projectFilter, categoryFilter, resultFilter]);

  const categories = [...new Set(safeArray(qaqcItems).map(i => i?.category).filter(Boolean))];
  const passCount = filteredItems.filter(i => i?.result === 'Pass').length;
  const failCount = filteredItems.filter(i => i.result === 'Fail').length;
  const conditionalCount = filteredItems.filter(i => i.result === 'Conditional').length;
  const showProjectColumn = projectFilter === 'all';

  const getResultClass = (result) => QAQC_RESULT_CLASS_MAP[result] || 'draft';
  const getStatusClass = (status) => QAQC_STATUS_CLASS_MAP[status] || 'draft';

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>QAQC Inspections</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const handleSaveInspection = async () => {
    if (!newInspection.project_id || !newInspection.activity.trim() || !newInspection.inspection_date) {
      notifyError('Project, activity and inspection date are required.');
      return;
    }
    if (formConfig.rulesEnabled) {
      const missing = missingRequiredFromEffects(ruleEffects, newInspection);
      if (missing.length) {
        const labels = missing.map((k) => formConfig.labelFor(k, k)).join(', ');
        notifyError(`Required by rule: ${labels}`);
        return;
      }
    }
    if (formConfig.customFieldsEnabled) {
      for (const f of customFields) {
        if (!f.required || f.hidden || ruleHidden(f.fieldKey)) continue;
        const v = customValues[f.fieldKey];
        if (v === undefined || v === null || v === '') {
          notifyError(`${f.label || f.fieldKey} is required.`);
          return;
        }
      }
    }
    if (editingId) {
      if (!(await confirmDialog('Save changes to this inspection?', 'Edit QAQC Inspection'))) return;
    } else {
      if (!(await confirmDialog('Save this inspection?', 'New QAQC Inspection'))) return;
    }
    try {
      const derivedStatus = newInspection.result === 'Pass' ? 'Approved' : (newInspection.result === 'Fail' ? 'Rework' : 'Under Review');
      const payload = {
        projectId: Number(newInspection.project_id),
        activity: newInspection.activity.trim(),
        result: newInspection.result,
        status: derivedStatus,
        data: {
          checklist_no: `QC-${Date.now().toString().slice(-6)}`,
          category: newInspection.category,
          inspection_date: newInspection.inspection_date,
          inspector: newInspection.inspector.trim() || '-',
          parameters_checked: newInspection.parameters_checked.trim() || '',
          defects_found: newInspection.defects_found.trim() || 'None',
          corrective_action: newInspection.corrective_action.trim() || '',
          retest_date: newInspection.retest_date || null,
          remarks: newInspection.remarks.trim() || '',
          attachments: newInspection.attachments || [],
        },
      };
      const response = editingId
        ? await qaqcAPI.update(editingId, payload)
        : await qaqcAPI.create(payload);
      const row = response?.data;
      if (formConfig.customFieldsEnabled && row?.id) {
        try {
          await adminAPI.saveCustomFieldValues('QAQCItem', row.id, {
            pageKey: 'qaqc',
            projectId: Number(newInspection.project_id),
            values: customValues,
            mode: editingId ? 'edit' : 'create',
          });
        } catch (cvErr) {
          console.error('Custom field save failed:', cvErr);
          notifyError(cvErr?.response?.data?.message || 'Inspection saved but custom fields failed.');
        }
      }
      const rowData = row.data || {};
      const mapped = {
        id: row.id,
        project_id: row.projectId ?? Number(newInspection.project_id),
        checklist_no: rowData.checklist_no ?? `QC-${row.id}`,
        activity: row.activity ?? newInspection.activity,
        category: rowData.category ?? newInspection.category,
        inspection_date: rowData.inspection_date ?? newInspection.inspection_date,
        inspector: rowData.inspector ?? newInspection.inspector,
        result: row.result ?? newInspection.result,
        parameters_checked: rowData.parameters_checked ?? newInspection.parameters_checked,
        defects_found: rowData.defects_found ?? newInspection.defects_found,
        corrective_action: rowData.corrective_action ?? newInspection.corrective_action,
        retest_date: rowData.retest_date ?? newInspection.retest_date,
        status: row.status ?? derivedStatus,
        remarks: rowData.remarks ?? newInspection.remarks,
        linked_correspondence: rowData.linked_correspondence ?? null,
        attachments: rowData.attachments ?? [],
      };
      setQaqcItems((prev) =>
        editingId ? prev.map((q) => (q.id === editingId ? mapped : q)) : [mapped, ...prev],
      );
      setShowModal(false);
      setEditingId(null);
      setNewInspection(blankInspection());
      setCustomValues({});
      notifySuccess(editingId ? 'Inspection updated successfully.' : 'Inspection saved successfully.');
    } catch (error) {
      console.error('Create QAQC inspection failed:', error);
      notifyError('Unable to save inspection. Please retry.');
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewInspection((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  return (
    <div>
        <div className="section-header">
        <h2>QAQC Register <span className="count-badge">{filteredItems.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'QAQC Register - Filtered', subtitle: `Project: ${projectFilter === 'all' ? 'All Projects' : (projects.find(p => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project')}`, element: tableRef.current })}
          >
            Download PDF
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              setEditingId(null);
              setNewInspection(blankInspection());
              setCustomValues(defaultsFromCustomFields());
              setShowModal(true);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            New Inspection
          </button>
        </div>
      </div>

      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <div className="kpi-card" onClick={() => setResultFilter('all')} style={{ cursor: 'pointer', borderColor: resultFilter === 'all' ? '#93c5fd' : undefined }}>
          <h4>Total Inspections</h4>
          <p className="kpi-value">{filteredItems.length}</p>
        </div>
        <div className="kpi-card" onClick={() => setResultFilter('Pass')} style={{ cursor: 'pointer', borderColor: resultFilter === 'Pass' ? '#86efac' : undefined }}>
          <h4>Pass</h4>
          <p className="kpi-value" style={{ color: '#047857' }}>{passCount}</p>
        </div>
        <div className="kpi-card" onClick={() => setResultFilter('Fail')} style={{ cursor: 'pointer', borderColor: resultFilter === 'Fail' ? '#fca5a5' : undefined }}>
          <h4>Fail / Rework</h4>
          <p className="kpi-value" style={{ color: '#b91c1c' }}>{failCount}</p>
        </div>
        <div className="kpi-card" onClick={() => setResultFilter('Conditional')} style={{ cursor: 'pointer', borderColor: resultFilter === 'Conditional' ? '#fcd34d' : undefined }}>
          <h4>Conditional</h4>
          <p className="kpi-value" style={{ color: '#b45309' }}>{conditionalCount}</p>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={setProjectFilter}
          projects={projects}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
          <option value="all">All Results</option>
          <option value="Pass">Pass</option>
          <option value="Fail">Fail</option>
          <option value="Conditional">Conditional</option>
        </select>
        <ResetFiltersButton
          onClick={() => {
            setProjectFilter('all');
            setCategoryFilter('all');
            setResultFilter('all');
          }}
        />
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              {showProjectColumn && <th>Project</th>}
              <th>Checklist No</th>
              <th>Activity</th>
              <th>Category</th>
              <th>Date</th>
              <th>Inspector</th>
              <th>Result</th>
              <th>Status</th>
              <th>Defects</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => (
              <tr key={item.id} className="clickable" onClick={() => setSelectedItem(item)}>
                {showProjectColumn && (
                  <td>{projects.find((p) => p.id === item.project_id)?.name || '-'}</td>
                )}
                <td style={{ fontWeight: 500, color: '#7C3AED' }}>{item.checklist_no}</td>
                <td style={{ fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.activity}</td>
                <td><StatusBadge tone={item.category === 'Foundation' ? 'open' : item.category === 'Pavement' ? 'medium' : item.category === 'Substructure' ? 'high' : 'active'}>{item.category}</StatusBadge></td>
                <td>{formatDateIN(item.inspection_date)}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{item.inspector}</td>
                <td><StatusBadge tone={getResultClass(item.result)}>{item.result}</StatusBadge></td>
                <td><StatusBadge tone={getStatusClass(item.status)}>{item.status}</StatusBadge></td>
                <td style={{ fontSize: 12, color: item.defects_found === 'None' ? '#9ca3af' : '#b91c1c', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.defects_found}</td>
                <td>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={async (e) => {
                      e.stopPropagation();
                      setEditingId(item.id);
                      setNewInspection({
                        project_id: String(item.project_id),
                        category: item.category,
                        activity: item.activity,
                        inspection_date: item.inspection_date,
                        inspector: item.inspector,
                        parameters_checked: item.parameters_checked,
                        result: item.result,
                        retest_date: item.retest_date || '',
                        defects_found: item.defects_found,
                        corrective_action: item.corrective_action || '',
                        remarks: item.remarks || '',
                        attachments: item.attachments || [],
                      });
                      setCustomValues(defaultsFromCustomFields());
                      if (formConfig.customFieldsEnabled) {
                        try {
                          const res = await adminAPI.getCustomFieldValues('QAQCItem', item.id);
                          setCustomValues({
                            ...defaultsFromCustomFields(),
                            ...(res?.data?.data?.values || res?.data?.values || {}),
                          });
                        } catch (_e) {
                          /* keep defaults */
                        }
                      }
                      setShowModal(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    style={{ marginLeft: 8, color: '#b91c1c', borderColor: '#fecaca' }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!(await confirmDialog('Delete this inspection?', 'Delete QAQC Inspection'))) return;
                      try {
                        await qaqcAPI.delete(item.id);
                        setQaqcItems((prev) => prev.filter((q) => q.id !== item.id));
                        notifySuccess('Inspection deleted.');
                      } catch (error) {
                        console.error('Delete QAQC inspection failed:', error);
                        notifyError('Unable to delete inspection. Please retry.');
                      }
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{selectedItem.checklist_no} - Inspection Detail</h2>
              <button className="btn-close" onClick={() => setSelectedItem(null)}><svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg></button>
            </div>
            <div className="modal-body">
              <LinkedCorrespondenceCard linkedCorrespondenceId={selectedItem.linked_correspondence} />
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', margin: '0 0 8px 0' }}>{selectedItem.activity}</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <StatusBadge tone={getResultClass(selectedItem.result)}>{selectedItem.result}</StatusBadge>
                <StatusBadge tone={getStatusClass(selectedItem.status)}>{selectedItem.status}</StatusBadge>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Category</p><p style={{ fontSize: 13, fontWeight: 500 }}>{selectedItem.category}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Inspection Date</p><p style={{ fontSize: 13 }}>{formatDateIN(selectedItem.inspection_date)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Inspector</p><p style={{ fontSize: 13, fontWeight: 500 }}>{selectedItem.inspector}</p></div>
                {selectedItem.retest_date && <div><p style={{ fontSize: 12, color: '#6b7280' }}>Retest Date</p><p style={{ fontSize: 13 }}>{formatDateIN(selectedItem.retest_date)}</p></div>}
              </div>

              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>Parameters Checked</p>
                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{selectedItem.parameters_checked}</p>
              </div>

              <div style={{ marginBottom: 16, padding: 12, background: selectedItem.defects_found === 'None' ? '#d1fae5' : '#fee2e2', borderRadius: 6 }}>
                <p style={{ fontSize: 12, color: selectedItem.defects_found === 'None' ? '#047857' : '#b91c1c', marginBottom: 4, fontWeight: 600 }}>Defects Found</p>
                <p style={{ fontSize: 13, color: selectedItem.defects_found === 'None' ? '#065f46' : '#991b1b' }}>{selectedItem.defects_found}</p>
              </div>

              {selectedItem.corrective_action && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>Corrective Action</p>
                  <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{selectedItem.corrective_action}</p>
                </div>
              )}

              {(selectedItem.attachments || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Attachments</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {selectedItem.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>{file?.name || `Attachment ${idx + 1}`}</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, `QAQC attachment: ${file?.name}`))}>View</button>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, `QAQC attachment: ${file?.name}`)}>Download</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>Remarks</p>
                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{selectedItem.remarks}</p>
              </div>
              {formConfig.customFieldsEnabled && customFields.length > 0 && (
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Additional fields</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {customFields.map((f) => (
                      <div key={f.fieldKey}>
                        <p style={{ fontSize: 12, color: '#6b7280' }}>{f.label}</p>
                        <p style={{ fontSize: 13, fontWeight: 500 }}>
                          {detailCustomValues[f.fieldKey] === true ? 'Yes'
                            : detailCustomValues[f.fieldKey] === false ? 'No'
                              : (detailCustomValues[f.fieldKey] != null && detailCustomValues[f.fieldKey] !== ''
                                ? String(detailCustomValues[f.fieldKey])
                                : '—')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>New QA/QC Inspection</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}><svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg></button>
            </div>
            <div className="modal-body">
              {!formConfig.fallback && (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>Using published form configuration v{formConfig.config?.versionNumber || '—'}</p>
              )}
              <div className="form-row">
                {!hide('project_id') && (
                  <div className="form-group"><label>{fl('project_id', 'Project')} {req('project_id', true) && <span className="req">*</span>}</label><select value={newInspection.project_id} onChange={(e) => setNewInspection((prev) => ({ ...prev, project_id: e.target.value }))}><option value="">Select</option>{projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                )}
                {!hide('category') && (
                  <div className="form-group"><label>{fl('category', 'Category')} {req('category', false) && <span className="req">*</span>}</label><select value={newInspection.category} onChange={(e) => setNewInspection((prev) => ({ ...prev, category: e.target.value }))}>{qaqcCategories.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                )}
              </div>
              {!hide('activity') && (
                <div className="form-group"><label>{fl('activity', 'Activity')} {req('activity', true) && <span className="req">*</span>}</label><input type="text" placeholder={ph('activity', 'e.g. Pile Foundation - Pier P-1')} value={newInspection.activity} onChange={(e) => setNewInspection((prev) => ({ ...prev, activity: e.target.value }))} /></div>
              )}
              <div className="form-row">
                {!hide('inspection_date') && (
                  <div className="form-group"><label>{fl('inspection_date', 'Inspection Date')} {req('inspection_date', true) && <span className="req">*</span>}</label><input type="date" value={newInspection.inspection_date} onChange={(e) => setNewInspection((prev) => ({ ...prev, inspection_date: e.target.value }))} /></div>
                )}
                {!hide('inspector') && (
                  <div className="form-group"><label>{fl('inspector', 'Inspector')} {req('inspector', false) && <span className="req">*</span>}</label><input type="text" placeholder={ph('inspector', 'Inspector name')} value={newInspection.inspector} onChange={(e) => setNewInspection((prev) => ({ ...prev, inspector: e.target.value }))} /></div>
                )}
              </div>
              {!hide('parameters_checked') && (
                <div className="form-group"><label>{fl('parameters_checked', 'Parameters Checked')}</label><textarea placeholder={ph('parameters_checked', 'List parameters checked...')} rows={2} value={newInspection.parameters_checked} onChange={(e) => setNewInspection((prev) => ({ ...prev, parameters_checked: e.target.value }))} /></div>
              )}
              <div className="form-row">
                {!hide('result') && (
                  <div className="form-group"><label>{fl('result', 'Result')}</label><select value={newInspection.result} onChange={(e) => setNewInspection((prev) => ({ ...prev, result: e.target.value }))}><option value="Pass">Pass</option><option value="Fail">Fail</option><option value="Conditional">Conditional</option></select></div>
                )}
                {!hide('retest_date') && (
                  <div className="form-group"><label>{fl('retest_date', 'Retest Date (if needed)')} {req('retest_date', false) && <span className="req">*</span>}</label><input type="date" value={newInspection.retest_date} disabled={readOnlyField('retest_date')} onChange={(e) => setNewInspection((prev) => ({ ...prev, retest_date: e.target.value }))} /></div>
                )}
              </div>
              {!hide('defects_found') && (
                <div className="form-group"><label>{fl('defects_found', 'Defects Found')}</label><textarea placeholder={ph('defects_found', "Describe defects or 'None'...")} rows={2} value={newInspection.defects_found} onChange={(e) => setNewInspection((prev) => ({ ...prev, defects_found: e.target.value }))} /></div>
              )}
              {!hide('corrective_action') && (
                <div className="form-group"><label>{fl('corrective_action', 'Corrective Action')}</label><textarea placeholder={ph('corrective_action', 'Action taken or required...')} rows={2} value={newInspection.corrective_action} onChange={(e) => setNewInspection((prev) => ({ ...prev, corrective_action: e.target.value }))} /></div>
              )}
              {!hide('remarks') && (
                <div className="form-group"><label>{fl('remarks', 'Remarks')}</label><textarea placeholder={ph('remarks', 'Additional remarks...')} rows={2} value={newInspection.remarks} onChange={(e) => setNewInspection((prev) => ({ ...prev, remarks: e.target.value }))} /></div>
              )}
              {formConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={customFields}
                  values={customValues}
                  onChange={onCustomChange}
                  title="Additional fields"
                />
              )}
              {!hide('attachments') && (
              <div className="form-group">
                <label>{fl('attachments', 'Attachments')}</label>
                <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleAttachmentChange} disabled={attachmentUploading} />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {newInspection.attachments?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {newInspection.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '4px 8px' }}>
                        {file?.name}
                        <span style={{ color: '#22c55e' }}>✓</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => setNewInspection((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, i) => i !== idx) }))}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleSaveInspection} disabled={attachmentUploading}>
                  {editingId ? 'Save Changes' : 'Save Inspection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QAQC;
